import { Nullable } from 'utils/tsHelpers';
import { MessageMedium } from 'constants/messageMediums';
import { MessageType } from 'api/MessageTypeAPI';
import { MediumConfig } from '../types';

/** Base types */
export type SupportedMediums = MessageMedium.Email | MessageMedium.SMS | MessageMedium.Push;

export enum FrequencyMode {
  Off = 'Off',
  NoCap = 'NoCap', // NoCap only applies to MessageTypes
  Cap = 'Cap',
  Optimize = 'Optimize',
}

export enum FrequencyPeriod {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}

export type DataExportStatus = 'Finished' | 'Running';
export type DataExportOptimizationType = 'Message Type' | 'Channel';
export interface CsvData {
  optimization_id: number | null;
  status: DataExportStatus;
  optimization_type: DataExportOptimizationType;
  channel: SupportedMediums;
  message_type: string | null;
  message_type_id: number | null;
  min_allowed: number;
  max_allowed: number;
  interval_days: number;
  start_date: string;
  end_date: string;
  optimal_cap: number | null;
  user_count: number | null;
  engagement_rate: number | null;
  unsubscribe_rate: number | null;
}

export type HistogramDataPoint = {
  capNumber: number;
  percentage: number;
};
export type HistogramData = {
  histogram_data_points: HistogramDataPoint[];
  csv_export_data: CsvData[];
};

/** @description Common fields across Message medium and Message type */
export interface FrequencyManagementBaseFields {
  /** @description Property containing json with histogram data and future attributes  */
  maxAllowed: number | null;
  minAllowed: number | null;
  mode: FrequencyMode | null;
  period: FrequencyPeriod | null;
}

/** @description Common fields across Message medium and Message type */
export interface FrequencyManagementBaseFields {
  /** @description Property containing json with histogram data and future attributes  */
  maxAllowed: number | null;
  minAllowed: number | null;
  mode: FrequencyMode | null;
  period: FrequencyPeriod | null;
}

export interface FrequencyManagementOptimizeAIData {
  frequencyOptimizationDetails: HistogramData | null;
  frequencyOptimizationDetailsUpdatedAt: string | null;
}

export interface FrequencyManagementSettings
  extends FrequencyManagementBaseFields,
    FrequencyManagementOptimizeAIData {}

export type MessageChannel = {
  name: string;
};

/** @description Message type */
export type FrequencyManagementMessageType = Pick<MessageType, 'id'> & {
  name: string;
  messageChannel: MessageChannel;
  frequencyManagement: {
    ignoreCap: boolean;
    settings: (FrequencyManagementBaseFields & FrequencyManagementOptimizeAIData) | null;
  };
};

export type ConfigByMediumSerialized = {
  currentProject: {
    id: number;
    settings: {
      frequencyManagement: {
        [mediumKey: string]: {
          isConfiguredForSending: boolean;
          settings: Nullable<FrequencyManagementSettings>;
          messageTypes: Array<FrequencyManagementMessageType>;
        };
      };
    };
  };
};

/** @description Parsed frequency management settings types */
export type ConfigByMedium = {
  [mediumKey in SupportedMediums]: MediumConfig;
};
